import React, { forwardRef, useRef } from "react";
import { Box, FormControl, FormControlLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, Skeleton, Stack } from "@mui/material";
import { SortOptions } from "@/types/search/tour-search-results";
import { useSearchParams } from "next/navigation";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslations } from "next-intl";
import useCloseOnScroll from "@/hooks/use-close-on-scroll";


interface SortOptionsProps {
  sortOptions: SortOptions[];
  onSortChange: (sortOrder: string) => void;
  loading?: boolean;
  currentSortOrder?: string;
  onFilterInteraction?: (filterType: string, filterValue: string | string[], action: "sort") => void;
  error?: boolean;
}



const CustomExpandIcon = forwardRef<SVGSVGElement, React.ComponentProps<typeof ExpandMoreIcon>>(
  (props, ref) => <ExpandMoreIcon {...props} ref={ref} style={{ cursor: "pointer" }} />
);


const SortOrder = ({ sortOptions, onSortChange, loading, currentSortOrder, error, onFilterInteraction }: SortOptionsProps) => {
  const t = useTranslations("SEARCH");
  const searchParams = useSearchParams();
  const urlSortOrder = searchParams.get("sortBy") || "";
  const [sortOrder, setSortOrder] = React.useState(() =>
    currentSortOrder !== undefined ? currentSortOrder : (urlSortOrder || (sortOptions.length > 0 ? sortOptions[0].value : ""))
  );
  // Store the last known displayName for the selected sortOrder
  const lastSelectedLabel = useRef<string>("");
  // State to track if dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  // Use the reusable hook to close dropdown on scroll
  const selectRef = useCloseOnScroll<HTMLDivElement>({
    isOpen: isDropdownOpen,
    onClose: () => setIsDropdownOpen(false),
    shouldBlurElement: true
  });

  React.useEffect(() => {
    if (currentSortOrder !== undefined) {
      // If currentSortOrder is provided (mobile mode), use it
      setSortOrder(currentSortOrder);
    } else {
      // Otherwise, use URL sort order (desktop mode)
      setSortOrder(urlSortOrder || (sortOptions.length > 0 ? sortOptions[0].value : ""));
    }
  }, [currentSortOrder, urlSortOrder, sortOptions]);
  const sortbytext = t("Sort By");
  const handleChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as string);
    onSortChange(event.target.value as string);
    onFilterInteraction?.("sortBy", event.target.value as string, "sort");
  };
  return (
    <Stack
      sx={{
        flexWrap: "wrap",
        justifyContent: "flex-start",
        gap: "1.2rem",
        flexDirection: "row",
        alignItems: "center"
      }}
      data-testid="sort by-filters"
    >
      <Box sx={{ minWidth: "30rem", maxWidth: "30.2rem" }}>
        <FormControl
          sx={{
            width: "30.2rem",
            "& .MuiOutlinedInput-root": {
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "inherit"
              },
              "&:focus-visible": {
                borderColor: "primary.main"
              }
            }
          }}
          data-testid="sort-form-control"
        >
          <InputLabel id="sort-select-label" sx={{ color: "text.primary", display: { xs: "none", legacyDesktop: "inline" } }} data-testid="sort-select-label">{sortbytext}</InputLabel>
          <Select
            ref={selectRef}
            open={isDropdownOpen}
            onOpen={() => setIsDropdownOpen(true)}
            onClose={() => setIsDropdownOpen(false)}
            variant="outlined"
            disabled={loading || error}
            sx={{
              borderRadius: "1rem",
              borderColor: "divider",
              display: {
                xs: "none",
                legacyDesktop: "block"
              },
              overflow: "hidden"
            }}
            IconComponent={CustomExpandIcon}
            labelId="sort-select-label"
            value={sortOrder}
            label={t("Sort By")}
            onChange={handleChange}
            data-testid="sort-select-dropdown"
            renderValue={() => {
              // If options are available, show the selected label as before
              const selectedOption = sortOptions.find(option => option.value === sortOrder);
              if (selectedOption) {
                lastSelectedLabel.current = selectedOption.displayName;
                return selectedOption.displayName;
              }
              // If options are not available (loading), show the last selected label (if any)
              if (sortOptions.length === 0 && lastSelectedLabel.current) {
                return lastSelectedLabel.current;
              }
              // If nothing has ever been selected, show an empty string (not 'Sort By')
              return "";
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: "background.default",
                  color: "grey.800",
                  borderRadius: "1rem",
                  borderTopColor: "divider",
                  borderBottomColor: "divider"
                }
              }
            }}
          >
            {sortOptions.map(option => (
              <MenuItem key={option.value} value={option.value} data-testid={`sort-option-${option.value}`}>
                {option.displayName}
              </MenuItem>
            ))}
          </Select>
          {loading === true || sortOptions.length === 0 ? (
            // Show skeleton placeholders during loading
            <Box sx={{ display: { xs: "block", legacyDesktop: "none" } }} data-testid="sort-skeleton-container">
              {Array.from({ length: 6 }, (_, index) => (
                <Box key={`sort-skeleton-${index}`} sx={{ display: "flex", alignItems: "center", mb: 1 }} data-testid={`sort-skeleton-${index + 1}`}>
                  <Skeleton variant="circular" width={20} height={20} sx={{ mr: 1 }} />
                  <Skeleton variant="text" width="60%" height={24} />
                </Box>
              ))}
            </Box>
          ) : (
            <RadioGroup
              aria-labelledby="demo-radio-buttons-group-label"
              value={(() => {
                // If sortOrder exists and matches an option, use it
                if (sortOrder && sortOptions.find(option => option.value === sortOrder)) {
                  const selectedOption = sortOptions.find(option => option.value === sortOrder);
                  if (selectedOption) {
                    lastSelectedLabel.current = selectedOption.displayName;
                    return sortOrder;
                  }
                }
                // If no sortOrder or it doesn't match any option, default to first option
                if (sortOptions.length > 0) {
                  const firstOption = sortOptions[0];
                  lastSelectedLabel.current = firstOption.displayName;
                  return firstOption.value;
                }
                // Fallback for when no options are available (loading state)
                return sortOrder || "";
              })()}
              name="radio-buttons-group"
              sx={{ display: { xs: "block", legacyDesktop: "none" } }}
              onChange={handleChange}
              data-testid="sort-radio-group"
            >
              {sortOptions.map(option => (
                <FormControlLabel
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "1.8rem" } }}
                  key={option.value}
                  value={option.value}
                  control={<Radio
                    sx={{ color: "text.primary" }} />}
                  label={option.displayName}
                  data-testid={`sort-radio-option-${option.value}`} />
              ))}
            </RadioGroup>
          )}
        </FormControl>
      </Box>
    </Stack>
  );
};
export default SortOrder;